import React, { useState, useEffect } from 'react';
import { Sparkles, Plus, RefreshCw, AlertTriangle, TrendingDown, History } from 'lucide-react';
import { useBusiness } from '../contexts/BusinessContext';
import { supabase } from '../lib/supabase';
import TokenPurchaseModal, { TOKEN_PACKAGES } from '../components/TokenPurchaseModal';
import {
  fetchAutoTopupSettings,
  saveAutoTopupSettings,
  resolveAutoTopupPause,
  runAutoTopupCheck,
  type AutoTopupSettings,
} from '../lib/aiTokenAutoTopup';
import toast from 'react-hot-toast';

interface TokenTransaction {
  id: string;
  transaction_type: 'purchase' | 'usage' | 'refund' | 'gift' | 'initial' | 'plan_allowance';
  tokens: number;
  balance_after: number | null;
  operation_type: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

/** Plan allowance + purchased tokens for this business (ai_token_budgets view). */
interface TokenBudget {
  tokenBalance: number;
  planIncluded: number;
  planUsed: number;
  planRemaining: number;
  available: number;
  planPercentUsed: number | null;
  planPeriodEnd: string | null;
}

const EMPTY_BUDGET: TokenBudget = {
  tokenBalance: 0,
  planIncluded: 0,
  planUsed: 0,
  planRemaining: 0,
  available: 0,
  planPercentUsed: null,
  planPeriodEnd: null,
};

/** Human-friendly names for the operation_type values features write. */
const OPERATION_LABELS: Record<string, string> = {
  ai_receptionist_call: 'AI receptionist call',
  sms_auto_reply: 'SMS auto-reply',
  email_auto_reply: 'Email auto-reply',
  call_transcription: 'Call transcription',
  auto_topup: 'Auto top-up',
  crm_lead_ai_split: 'CRM lead AI',
  lead_agent_turn: 'Lead agent reply',
  agent_training: 'Agent training',
  agent_scenario: 'Agent test scenario',
  automation_builder: 'AI automation builder',
  gbp_post_auto_generate: 'Google Business post',
  social_post_auto_generate: 'Social media post',
  gbp_review_auto_reply: 'Review auto-reply',
  text_edit: 'AI text edit',
  actions_summary: 'Actions summary',
  agent_turn: 'Agent',
  agent_computer: 'Agent computer',
  agent_web_search: 'Agent web search',
  website_edit: 'Website edit',
  agent_dictation: 'Voice typing',
  report_explorer: 'Dashboard report question',
  plan_allowance_reset: 'Plan AI usage renewed',
  plan_allowance_change: 'Plan AI usage updated',
  pick_video_cover: 'Video cover pick',
  expense_categorize: 'Expense categorization',
  vin_scan: 'VIN scan',
  business_card_scan: 'Business card scan',
};

function describeTransaction(tx: TokenTransaction): string {
  if (tx.transaction_type === 'plan_allowance') {
    return tx.operation_type === 'plan_allowance_change' ? 'Plan AI usage updated' : 'Plan AI usage renewed';
  }
  if (tx.operation_type && OPERATION_LABELS[tx.operation_type]) {
    return OPERATION_LABELS[tx.operation_type];
  }
  if (tx.operation_type) {
    const pretty = tx.operation_type.replace(/_/g, ' ');
    return pretty.charAt(0).toUpperCase() + pretty.slice(1);
  }
  switch (tx.transaction_type) {
    case 'purchase': return 'Token purchase';
    case 'initial': return 'Welcome tokens';
    case 'gift': return 'Bonus tokens';
    case 'refund': return 'Refund';
    default: return 'AI usage';
  }
}

function transactionDetail(tx: TokenTransaction): string | null {
  const meta = tx.metadata || {};
  if (tx.transaction_type === 'plan_allowance') {
    const included = Number(meta.included ?? tx.tokens);
    return Number.isFinite(included) && included > 0 ? `${included.toLocaleString()} included this period` : null;
  }
  if (tx.operation_type === 'ai_receptionist_call') {
    const secs = Number(meta.duration_seconds);
    if (Number.isFinite(secs) && secs > 0) {
      const m = Math.floor(secs / 60);
      const s = Math.round(secs % 60);
      return m > 0 ? `${m}m ${s}s call` : `${s}s call`;
    }
    return null;
  }
  if (tx.operation_type === 'call_transcription') {
    const mins = Number(meta.billed_minutes ?? meta.minutes);
    if (Number.isFinite(mins) && mins > 0) return `${mins} min transcribed`;
  }
  return null;
}

export default function AISettings() {
  const { selectedBusiness } = useBusiness();
  const [budget, setBudget] = useState<TokenBudget>(EMPTY_BUDGET);
  const tokenBalance = budget.tokenBalance;
  const [lifetimeUsed, setLifetimeUsed] = useState<number>(0);
  const [usedLast30, setUsedLast30] = useState<number>(0);
  const [transactions, setTransactions] = useState<TokenTransaction[]>([]);
  const [txFilter, setTxFilter] = useState<'all' | 'usage' | 'credits'>('all');
  const [loading, setLoading] = useState(true);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);

  // Auto top-up settings state
  const [autoTopup, setAutoTopup] = useState<AutoTopupSettings | null>(null);
  const [atEnabled, setAtEnabled] = useState(false);
  const [atThreshold, setAtThreshold] = useState('');
  const [atTarget, setAtTarget] = useState('');
  const [atLimit, setAtLimit] = useState('');
  const [atSaving, setAtSaving] = useState(false);
  const [atBusy, setAtBusy] = useState(false);

  useEffect(() => {
    if (selectedBusiness?.id) {
      fetchTokenData();
    }
  }, [selectedBusiness?.id]);

  const fetchTokenData = async () => {
    if (!selectedBusiness?.id) return;

    setLoading(true);
    try {
      const [{ data: tokenData, error: tokenError }, txQ, statsQ] = await Promise.all([
        supabase
          .from('ai_token_budgets')
          .select('token_balance, plan_tokens_included, plan_tokens_used, plan_tokens_remaining, available_tokens, plan_percent_used, plan_period_end')
          .eq('business_id', selectedBusiness.id)
          .maybeSingle(),
        supabase
          .from('ai_token_transactions')
          .select('id, transaction_type, tokens, balance_after, operation_type, metadata, created_at')
          .eq('business_id', selectedBusiness.id)
          .order('created_at', { ascending: false })
          .limit(100),
        // Both usage figures come from the ledger (single source of truth) —
        // ai_tokens.total_tokens_used is a drifted counter, don't trust it.
        supabase.rpc('get_ai_token_usage_stats', { p_business_id: selectedBusiness.id }),
      ]);

      if (tokenError) throw tokenError;

      if (tokenData) {
        setBudget({
          tokenBalance: Number(tokenData.token_balance) || 0,
          planIncluded: Number(tokenData.plan_tokens_included) || 0,
          planUsed: Number(tokenData.plan_tokens_used) || 0,
          planRemaining: Number(tokenData.plan_tokens_remaining) || 0,
          available: Number(tokenData.available_tokens) || 0,
          planPercentUsed:
            tokenData.plan_percent_used == null ? null : Number(tokenData.plan_percent_used),
          planPeriodEnd: tokenData.plan_period_end ?? null,
        });
      }
      setTransactions((txQ.data as TokenTransaction[]) || []);

      const statsRow = Array.isArray(statsQ.data) ? statsQ.data[0] : statsQ.data;
      if (!statsQ.error && statsRow) {
        setUsedLast30(Number(statsRow.used_last_30) || 0);
        setLifetimeUsed(Number(statsRow.used_all_time) || 0);
      } else {
        // RPC not installed yet — sum the ledger client-side.
        const { data: usageRows } = await supabase
          .from('ai_token_transactions')
          .select('tokens, created_at')
          .eq('business_id', selectedBusiness.id)
          .eq('transaction_type', 'usage')
          .limit(5000);
        const cutoff = Date.now() - 30 * 86400000;
        let last30 = 0;
        let allTime = 0;
        for (const row of (usageRows as { tokens: number; created_at: string }[]) || []) {
          const t = Math.abs(row.tokens || 0);
          allTime += t;
          if (new Date(row.created_at).getTime() >= cutoff) last30 += t;
        }
        setUsedLast30(last30);
        setLifetimeUsed(allTime);
      }

      const at = await fetchAutoTopupSettings(selectedBusiness.id);
      if (at) {
        setAutoTopup(at);
        setAtEnabled(at.enabled);
        setAtThreshold(at.threshold != null ? String(at.threshold) : '');
        // Only surface targets that match one of the set package amounts —
        // anything else (saved before amounts were fixed) forces a re-pick.
        setAtTarget(
          at.target != null && TOKEN_PACKAGES.some((p) => p.tokens === at.target)
            ? String(at.target)
            : '',
        );
        setAtLimit(at.monthlyLimit != null ? String(at.monthlyLimit) : '');
      }
    } catch (error) {
      console.error('Error fetching token data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAutoTopup = async () => {
    if (!selectedBusiness?.id || atSaving) return;
    const threshold = Number(atThreshold) || 0;
    const target = Number(atTarget) || 0;
    const limit = atLimit.trim() ? Number(atLimit) : null;

    if (atEnabled) {
      if (threshold < 100) { toast.error('Threshold must be at least 100 tokens'); return; }
      if (!TOKEN_PACKAGES.some((p) => p.tokens === target)) {
        toast.error('Choose one of the set top-up amounts');
        return;
      }
      if (target <= threshold) { toast.error('Top-up target must be greater than the threshold'); return; }
      if (limit != null && limit < target - threshold) {
        toast.error('Monthly limit must allow at least one top-up');
        return;
      }
    }

    setAtSaving(true);
    try {
      await saveAutoTopupSettings({
        businessId: selectedBusiness.id,
        enabled: atEnabled,
        threshold: atEnabled ? threshold : null,
        target: atEnabled ? target : null,
        monthlyLimit: atEnabled ? limit : null,
      });
      toast.success(atEnabled ? 'Auto top-up saved' : 'Auto top-up turned off');
      // Run a check right away — if they're already under the threshold this
      // tops up immediately.
      if (atEnabled) {
        const result = await runAutoTopupCheck(selectedBusiness.id);
        if (result.toppedUp) {
          toast.success(`Topped up ${result.tokensAdded?.toLocaleString()} tokens`);
        } else if (result.paymentFailed) {
          toast.error('Auto top-up payment failed — check your card on file.');
        }
      }
      await fetchTokenData();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not save auto top-up');
    } finally {
      setAtSaving(false);
    }
  };

  const handleApproveMore = async () => {
    if (!selectedBusiness?.id || atBusy) return;
    setAtBusy(true);
    try {
      await resolveAutoTopupPause(selectedBusiness.id, 'approve_more');
      toast.success('Approved — auto top-up resumed for this period');
      const result = await runAutoTopupCheck(selectedBusiness.id);
      if (result.toppedUp) toast.success(`Topped up ${result.tokensAdded?.toLocaleString()} tokens`);
      await fetchTokenData();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not resume auto top-up');
    } finally {
      setAtBusy(false);
    }
  };

  const handleRetryPayment = async () => {
    if (!selectedBusiness?.id || atBusy) return;
    setAtBusy(true);
    try {
      await resolveAutoTopupPause(selectedBusiness.id, 'retry_clear');
      const result = await runAutoTopupCheck(selectedBusiness.id);
      if (result.toppedUp) {
        toast.success(`Payment went through — topped up ${result.tokensAdded?.toLocaleString()} tokens`);
      } else if (result.paymentFailed) {
        toast.error('Payment failed again — update your card on file and retry.');
      } else {
        toast.success('Auto top-up re-enabled');
      }
      await fetchTokenData();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Retry failed');
    } finally {
      setAtBusy(false);
    }
  };

  const formatNumber = (num: number) => {
    // Always show exact token count with comma separators
    return num.toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto bg-gray-50">
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-100 rounded-xl">
              <Sparkles className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">AI Settings</h1>
              <p className="text-sm text-gray-600">
                Your plan includes AI usage every billing period. Purchased tokens are only used once that is at 100%.
              </p>
            </div>
          </div>
        </div>

        {/* Plan AI usage */}
        {budget.planIncluded > 0 ? (
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm" data-assistant-anchor="plan-ai-usage">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
              <div>
                <div className="text-sm font-medium text-gray-600">Plan AI usage</div>
                <div className="text-3xl font-bold text-gray-900 mt-1">
                  {budget.planPercentUsed ?? 0}%
                  <span className="text-base font-medium text-gray-500"> used</span>
                </div>
              </div>
              <div className="text-right text-xs text-gray-500">
                {budget.planPeriodEnd
                  ? `Resets ${new Date(budget.planPeriodEnd).toLocaleDateString([], { month: 'short', day: 'numeric' })}`
                  : 'Resets on your next bill'}
              </div>
            </div>
            <div className="h-3 w-full rounded-full bg-gray-100 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${
                  (budget.planPercentUsed ?? 0) >= 100
                    ? 'bg-red-500'
                    : (budget.planPercentUsed ?? 0) >= 80
                      ? 'bg-amber-500'
                      : 'bg-blue-600'
                }`}
                style={{ width: `${Math.min(100, Math.max(0, budget.planPercentUsed ?? 0))}%` }}
              />
            </div>
            {(budget.planPercentUsed ?? 0) >= 100 && (
              <p className="mt-3 text-sm text-amber-700">
                Your plan's AI usage is used up for this period.{' '}
                {tokenBalance > 0
                  ? 'AI features are now drawing from your purchased tokens.'
                  : 'Top up or upgrade your plan to keep AI features running.'}
              </p>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <div className="text-sm font-medium text-gray-600">Plan AI usage</div>
            <p className="mt-1 text-sm text-gray-500">
              Your current plan does not include AI usage. AI features run on purchased tokens.
            </p>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {/* Purchased tokens */}
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-medium text-gray-600">Purchased Tokens</div>
              <Sparkles className="h-5 w-5 text-blue-600" />
            </div>
            <div className="text-3xl font-bold text-gray-900">
              {formatNumber(tokenBalance)}
            </div>
            <div className="text-xs text-gray-500 mt-1">Used after your plan usage runs out</div>
          </div>
          {/* Used last 30 days */}
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-medium text-gray-600">Used — Last 30 Days</div>
              <TrendingDown className="h-5 w-5 text-amber-500" />
            </div>
            <div className="text-3xl font-bold text-gray-900">
              {formatNumber(usedLast30)}
            </div>
          </div>
          {/* Lifetime used */}
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-medium text-gray-600">Used — All Time</div>
              <History className="h-5 w-5 text-gray-400" />
            </div>
            <div className="text-3xl font-bold text-gray-900">
              {formatNumber(lifetimeUsed)}
            </div>
          </div>
        </div>

        {/* Purchase Tokens Section */}
        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Purchase More Tokens</h2>
          <p className="text-sm text-gray-600 mb-6">
            Purchased tokens never expire and are only used once your plan's AI usage for the period is at 100%.
          </p>
          
          <button
            onClick={() => setShowPurchaseModal(true)}
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
            data-assistant-anchor="top-up-tokens"
          >
            <Plus className="h-5 w-5" />
            Top Up Tokens
          </button>
        </div>

        {/* Auto Top-Up Section */}
        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-lg font-semibold text-gray-900">Configure Auto Top-Up</h2>
            <button
              type="button"
              role="switch"
              aria-checked={atEnabled}
              onClick={() => setAtEnabled((v) => !v)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors ${
                atEnabled ? 'bg-blue-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                  atEnabled ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
          <p className="text-sm text-gray-600 mb-5">
            Never run out mid-task — when what you have left (plan usage plus purchased tokens) drops below your
            threshold, we automatically charge your card on file and add tokens. Billed at the standard token rate.
          </p>

          {/* Paused banners */}
          {autoTopup?.pausedReason === 'limit_reached' && (
            <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
              <div className="flex items-start gap-2 text-sm text-amber-800">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>
                  Auto top-up is <span className="font-semibold">paused</span> — you hit your monthly limit
                  {autoTopup.monthlyLimit ? ` of ${autoTopup.monthlyLimit.toLocaleString()} tokens` : ''}. It resumes
                  automatically next month, or approve more for this period now.
                </span>
              </div>
              <button
                onClick={handleApproveMore}
                disabled={atBusy}
                className="shrink-0 px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-sm font-semibold text-white transition-colors disabled:opacity-50"
              >
                {atBusy ? 'Working…' : 'Approve more'}
              </button>
            </div>
          )}
          {(autoTopup?.pausedReason === 'payment_failed' || autoTopup?.pausedReason === 'payment_failed_ack') && (
            <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
              <div className="flex items-start gap-2 text-sm text-red-800">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>
                  Auto top-up is <span className="font-semibold">paused — the last payment failed</span>
                  {autoTopup.lastError ? `: ${autoTopup.lastError}` : '.'}
                </span>
              </div>
              <button
                onClick={handleRetryPayment}
                disabled={atBusy}
                className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-sm font-semibold text-white transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${atBusy ? 'animate-spin' : ''}`} />
                {atBusy ? 'Retrying…' : 'Retry payment'}
              </button>
            </div>
          )}

          <div className={`grid sm:grid-cols-3 gap-4 ${atEnabled ? '' : 'opacity-50 pointer-events-none'}`}>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">When remaining usage falls below</label>
              <input
                type="number"
                min={100}
                step={500}
                value={atThreshold}
                onChange={(e) => setAtThreshold(e.target.value)}
                placeholder="e.g. 2,000"
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30"
              />
              <p className="text-[11px] text-gray-400 mt-1">tokens</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Top up to</label>
              <select
                value={atTarget}
                onChange={(e) => setAtTarget(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/30"
              >
                <option value="">Select amount…</option>
                {TOKEN_PACKAGES.map((pkg) => (
                  <option key={pkg.tokens} value={pkg.tokens}>
                    {pkg.displayTokens} tokens (${pkg.price})
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-gray-400 mt-1">tokens</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Monthly limit (optional)</label>
              <input
                type="number"
                min={1000}
                step={1000}
                value={atLimit}
                onChange={(e) => setAtLimit(e.target.value)}
                placeholder="e.g. 100,000"
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30"
              />
              <p className="text-[11px] text-gray-400 mt-1">
                Pause if auto top-up exceeds this many tokens in a month
                {autoTopup && autoTopup.periodPurchased > 0
                  ? ` · ${autoTopup.periodPurchased.toLocaleString()} used this month`
                  : ''}
              </p>
            </div>
          </div>

          <button
            onClick={handleSaveAutoTopup}
            disabled={atSaving}
            className="mt-5 inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {atSaving ? 'Saving…' : 'Save auto top-up'}
          </button>
        </div>

        {/* Usage & Activity */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-6 pt-6 pb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Usage</h2>
              <p className="text-sm text-gray-600 mt-0.5">
                Every token movement on your account — AI features, receptionist calls, purchases and top-ups.
              </p>
            </div>
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 shrink-0">
              {([['all', 'All'], ['usage', 'Usage'], ['credits', 'Credits']] as const).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setTxFilter(key)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    txFilter === key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {(() => {
            const filtered = transactions.filter((tx) => {
              if (txFilter === 'usage') return tx.transaction_type === 'usage';
              if (txFilter === 'credits') return tx.transaction_type !== 'usage';
              return true;
            });
            if (filtered.length === 0) {
              return (
                <div className="px-6 py-10 text-center text-sm text-gray-500 border-t border-gray-100">
                  No activity yet — usage will show up here as your AI features run.
                </div>
              );
            }
            return (
              <div className="divide-y divide-gray-100 border-t border-gray-100 max-h-[480px] overflow-y-auto">
                {filtered.map((tx) => {
                  const isDebit = tx.tokens < 0;
                  const isPlanEvent = tx.transaction_type === 'plan_allowance';
                  const source = String(tx.metadata?.source || '');
                  const detail = transactionDetail(tx);
                  const sourceLabel =
                    isDebit && source === 'plan'
                      ? 'plan usage'
                      : isDebit && source === 'purchased'
                        ? 'purchased tokens'
                        : isDebit && source === 'mixed'
                          ? 'plan + purchased'
                          : null;
                  return (
                    <div key={tx.id} className="flex items-center gap-3 px-6 py-3">
                      <div
                        className={`w-2 h-2 rounded-full shrink-0 ${
                          isDebit ? 'bg-amber-400' : isPlanEvent ? 'bg-blue-500' : 'bg-green-500'
                        }`}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {describeTransaction(tx)}
                          {detail && <span className="text-gray-500 font-normal"> · {detail}</span>}
                        </div>
                        <div className="text-xs text-gray-500">
                          {new Date(tx.created_at).toLocaleString([], {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                          })}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        {isPlanEvent ? null : (
                          <div className={`text-sm font-semibold ${isDebit ? 'text-gray-900' : 'text-green-600'}`}>
                            {isDebit ? '−' : '+'}{Math.abs(tx.tokens).toLocaleString()}
                          </div>
                        )}
                        {sourceLabel ? (
                          <div className="text-[11px] text-gray-400">{sourceLabel}</div>
                        ) : !isPlanEvent && tx.balance_after != null ? (
                          <div className="text-[11px] text-gray-400">
                            {tx.balance_after.toLocaleString()} purchased left
                          </div>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      </div>

      {/* Token Purchase Modal */}
      {selectedBusiness?.id && (
        <TokenPurchaseModal
          isOpen={showPurchaseModal}
          onClose={() => setShowPurchaseModal(false)}
          businessId={selectedBusiness.id}
          onSuccess={fetchTokenData}
        />
      )}
    </div>
  );
}

